import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DatePicker } from 'primeng/datepicker';
import { Subject, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { WarningMessageComponent, WarningMessage } from '../../common/warning-message/warning-message.component';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { pickRole21SchemaFile } from '../../../services/mbrs/validators/role21-notes-accounting-policies.rules';
import {
  MFRS_ACCOUNTING_STANDARD,
  MPERS_ACCOUNTING_STANDARD,
} from '../../../services/mbrs/validators/schema-picker.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  required?: boolean;
  ui: {
    control: string;
    level?: number;
    note?: string;
    rows?: number;
    placeholder?: string;
    options?: string[];
    enumSource?: string;
    conditionalOn?: string;
    conditionalValue?: string;
  };
  error?: {
    required?: string;
    pattern?: string;
    min?: string;
    max?: string;
    [key: string]: string | undefined;
  };
}

/** Shared empty-list sentinel for getAllWarnings() so fields without warnings return a stable reference. */
const EMPTY_WARNINGS: any[] = [];

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role21-720000-notes-accounting-policies',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputText,
    ButtonModule,
    Toast,
    DatePicker,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    WarningMessageComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role21-720000-notes-accounting-policies.component.html',
  styleUrls: ['./role21-720000-notes-accounting-policies.component.css']
})
export class Role21720000NotesAccountingPoliciesComponent implements OnInit, OnDestroy {
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  form!: FormGroup;
  schema!: FormSchema;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Notes - Summary of Material Accounting Policies (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.notesAccountingPolicies
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  isLoading = true;
  private isLoadingState = false;

  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint blanks a date the user is half-way through typing.
   * Mirrors role23/role24, which carry the identical listener and guard.
   */
  private isInitialLoadComplete = false;

  /**
   * Per-field cache of the warning list rendered to `<app-warning-message>`. Rebuilt
   * only when evaluateFieldWarnings() runs so the template hands the same array
   * reference to Angular on each change-detection tick. An empty-array sentinel is
   * returned for fields without warnings so consumers get a stable reference there too.
   */
  private allWarningsCache: { [qname: string]: WarningMessage[] } = {};

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '21',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupConditionalFieldLogic();
      this.setupCrossReferenceAutoPopulation();
      this.setupWarningEvaluation();
      this.evaluateFieldWarnings();
      // Reveal the sheet's already-computed errors now that the form exists and
      // has been populated. Replaces this sheet's own `markRequiredFieldsAsTouched`,
      // which wrapped its work in a bare setTimeout(100) and only ever revealed
      // schema-`required` fields — a pattern/format error stayed invisible until
      // the preparer happened to touch the field.
      revealValidationErrors(this.form, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.notesAccountingPolicies),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((notesAccountingPoliciesData) => {
        if (notesAccountingPoliciesData && Object.keys(notesAccountingPoliciesData).length > 0) {
          // After initial load completes, skip re-patching. `state$` is the
          // MODIFIED stream, so this sheet's own debounced persist comes
          // straight back here — and `patchValue` reaches PrimeNG's
          // `writeValue`, which re-renders the input from the model even under
          // `emitEvent: false`. While a date is half-typed that model is null
          // (`DatePicker.onUserInput` calls `updateModel(null)` on text its
          // `parseValueFromString` rejects), so the echo blanks the digits the
          // user just entered. Adopting a SIBLING TAB's values is
          // `adoptRemoteState()`'s job, on `remoteUpdateApplied$`.
          // Mirrors role12/role23/role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // NO date conversion needed for this form - all fields are text

          // Patch the form with the new data
          this.form.patchValue(notesAccountingPoliciesData, { emitEvent: false });

          // NOTE: Cross-reference auto-population logic already checks if fields are empty
          // So backend data will NOT be overwritten by cross-reference data
          // The setupCrossReferenceAutoPopulation() method checks isEmpty at line 333

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load schema from JSON file (dynamically selects MFRS or MPERS)
   */
  private async loadSchema(): Promise<void> {
    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role21');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Get the accounting standard from filing info
    const filingData = this.stateService.getActiveFilingInfoData();
    const accountingStandard = filingData['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';

    // Delegate the MFRS/MPERS decision to the shared rules module so the
    // pure validator and this component use identical selection logic.
    const schemaFile = pickRole21SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      `/assets/forms/${schemaFile}`,
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    const formControls: any = {};

    if (this.schema && this.schema.controls) {
      this.schema.controls.forEach(control => {
        if (control.ui.control !== 'section-header') {
          const defaultValue = this.getDefaultValue(control);
          const validators = this.getValidators(control);
          formControls[control.qname] = new FormControl(defaultValue, validators);
        }
      });
    }

    this.form = new FormGroup(formControls);
  }

  /**
   * Get default value for control based on type
   */
  private getDefaultValue(control: SchemaControl): any {
    switch (control.type) {
      case 'YesNoItemType':
        return null;
      case 'TextBlock':
      case 'StringItemType':
      default:
        return '';
    }
  }

  /**
   * Get validators for control
   */
  private getValidators(control: SchemaControl): any[] {
    const validators: any[] = [];

    // Add required validator if field is marked as required
    if (control.required) {
      validators.push(Validators.required);
    }

    return validators;
  }

  /**
   * Setup conditional field logic — currently a no-op. Conditional visibility is
   * handled reactively via `shouldDisplayField()` reading the parent form control,
   * so an explicit subscription is not required. Method retained as a seam for
   * future per-field side-effects tied to the "Other accounting policies" toggle.
   */
  private setupConditionalFieldLogic(): void {
    // Intentionally empty. See method doc.
  }

  /**
   * Check if field should be displayed based on conditional logic
   */
  shouldDisplayField(control: SchemaControl): boolean {
    if (!control.ui.conditionalOn) {
      return true;
    }

    const parentControl = this.form?.get(control.ui.conditionalOn);
    if (!parentControl) {
      return true;
    }

    return parentControl.value === control.ui.conditionalValue;
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.notesAccountingPolicies);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.notesAccountingPolicies);
    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });
    }

    // Small delay to ensure form is fully initialized before enabling auto-save
    setTimeout(() => {
      this.isLoadingState = false;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        // Re-baseline the transformed form value (esp. the rich-text field's on-load
        // fragment normalization) so the snapshot equals what the auto-save persist
        // writes; without it a full revert never cleared the pencil.
        this.stateService.updateSheetData(
          SHEET_KEYS.notesAccountingPolicies,
          this.form.value,
          this.form.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
        this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.notesAccountingPolicies, !this.form.valid);
        // Initial load has settled — from here the state listener stops re-patching.
        this.isInitialLoadComplete = true;
      }, 600);
    }, 100);
  }

  /**
   * Setup automatic state persistence
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        this.stateService.updateSheetData(SHEET_KEYS.notesAccountingPolicies, this.form.value, this.form.valid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.notesAccountingPolicies, { hasErrors: !this.form.valid });
      },
    });
  }

  /**
   * Auto-fill demo data
   */
  autoFillDemo(): void {
    const demoData: any = {
      'ssmt:DescriptionOfBasisOfPreparation': 'The financial statements have been prepared in accordance with the historical cost convention, except as disclosed in the accounting policies below.\n\nThe financial statements are presented in Ringgit Malaysia (RM) and all values are rounded to the nearest thousand (RM\'000) except when otherwise indicated.',
      'ssmt:StatementOfComplianceWithMFRS': 'The financial statements of the Company have been prepared in accordance with Malaysian Private Entities Reporting Standards (MPERS) issued by the Malaysian Accounting Standards Board (MASB) and the requirements of the Companies Act 2016 in Malaysia.',
      'ssmt:DescriptionOfFunctionalCurrency': 'The functional currency of the Company is Ringgit Malaysia (RM), which is the currency of the primary economic environment in which the Company operates.',
      'ssmt:DescriptionOfPresentationCurrency': 'These financial statements are presented in Ringgit Malaysia (RM), which is the Company\'s functional and presentation currency.',
      'ssmt:DescriptionOfUseOfEstimatesAndJudgements': 'The preparation of financial statements in conformity with MPERS requires management to make judgements, estimates and assumptions that affect the application of accounting policies and the reported amounts of assets, liabilities, income and expenses.\n\nActual results may differ from these estimates. Estimates and underlying assumptions are reviewed on an ongoing basis. Revisions to accounting estimates are recognised in the period in which the estimates are revised and in any future periods affected.',
      'ssmt:DescriptionOfRevenueRecognitionPolicies': 'Revenue is measured at the fair value of consideration received or receivable.\n\n(a) Sale of goods\nRevenue from sale of goods is recognised when the Company has transferred to the buyer the significant risks and rewards of ownership of the goods, which generally coincides with delivery and acceptance of the goods sold.\n\n(b) Rendering of services\nRevenue from services rendered is recognised in the accounting period in which the services are rendered, by reference to the stage of completion of the specific transaction assessed on the basis of the actual service provided as a proportion of the total services to be provided.\n\n(c) Dividend income\nDividend income is recognised when the Company\'s right to receive payment is established.\n\n(d) Interest income\nInterest income is recognised on an accrual basis using the effective interest method.',
      'ssmt:DescriptionOfAccountingPolicyForPropertyPlantAndEquipment': 'Property, plant and equipment are stated at cost less accumulated depreciation and accumulated impairment losses.\n\nCost includes expenditure that is directly attributable to the acquisition of the asset. The cost of self-constructed assets includes the cost of materials and direct labour, any other costs directly attributable to bringing the assets to a working condition for their intended use, the costs of dismantling and removing the items and restoring the site on which they are located, and borrowing costs on qualifying assets.\n\nWhen significant parts of an item of property, plant and equipment have different useful lives, they are accounted for as separate items (major components) of property, plant and equipment.\n\nDepreciation is calculated to write off the cost of items of property, plant and equipment less their estimated residual values using the straight-line basis over their estimated useful lives. Depreciation of an asset does not cease when the asset becomes idle or is retired from active use unless the asset is fully depreciated.\n\nEstimated useful lives:\n- Buildings: 50 years\n- Plant and machinery: 10-15 years\n- Motor vehicles: 5 years\n- Furniture, fixtures and fittings: 5-10 years\n- Office equipment: 3-5 years',
      'ssmt:DescriptionOfAccountingPolicyForIntangibleAssets': 'Intangible assets acquired separately are measured on initial recognition at cost. The cost of intangible assets acquired in a business combination is their fair value at the date of acquisition.\n\nIntangible assets with finite useful lives are amortised over the useful economic life and assessed for impairment whenever there is an indication that the intangible asset may be impaired. The amortisation period and the amortisation method are reviewed at least at the end of each reporting period.\n\nComputer software is stated at cost less accumulated amortisation and accumulated impairment losses. Amortisation is calculated on a straight-line basis over the estimated useful life of 3 to 5 years.',
      'ssmt:DescriptionOfAccountingPolicyForFinancialInstruments': 'Financial assets and financial liabilities are recognised when the Company becomes a party to the contractual provisions of the instruments.\n\nFinancial assets and financial liabilities are initially measured at fair value. Transaction costs that are directly attributable to the acquisition or issue of financial assets and financial liabilities are added to or deducted from the fair value of the financial assets or financial liabilities, as appropriate, on initial recognition.\n\nFinancial assets are classified as either financial assets at fair value through profit or loss, loans and receivables, held-to-maturity investments, or available-for-sale financial assets, as appropriate. Financial liabilities are classified as either financial liabilities at fair value through profit or loss or other financial liabilities.\n\nThe Company derecognises a financial asset when the contractual rights to the cash flows from the asset expire, or when it transfers the financial asset and substantially all the risks and rewards of ownership of the asset to another party.',
      'ssmt:DescriptionOfAccountingPolicyForInventories': 'Inventories are stated at the lower of cost and net realisable value.\n\nCost is determined using the first-in, first-out (FIFO) method. The cost of finished goods and work in progress comprises raw materials, direct labour, other direct costs and related production overheads (based on normal operating capacity). It excludes borrowing costs.\n\nNet realisable value is the estimated selling price in the ordinary course of business, less the estimated costs of completion and the estimated costs necessary to make the sale.',
      'ssmt:DescriptionOfAccountingPolicyForCashAndCashEquivalents': 'Cash and cash equivalents consist of cash on hand, balances and deposits with banks which have insignificant risk of changes in fair value with original maturities of three months or less, and are used by the Company in the management of their short term commitments. For the purpose of the statement of cash flows, cash and cash equivalents are presented net of bank overdrafts and pledged deposits.',
      'ssmt:DescriptionOfAccountingPolicyForForeignCurrencyTranslation': 'Transactions in foreign currencies are translated to the respective functional currencies of the Company at exchange rates at the dates of the transactions. Monetary assets and liabilities denominated in foreign currencies at the reporting date are retranslated to the functional currency at the exchange rate at that date.\n\nNon-monetary assets and liabilities denominated in foreign currencies that are measured at fair value are retranslated to the functional currency at the exchange rate at the date that the fair value was determined. Non-monetary items in a foreign currency that are measured in terms of historical cost are translated using the exchange rate at the date of the transaction.\n\nForeign currency differences arising on retranslation are recognised in profit or loss.',
      'ssmt:DescriptionOfAccountingPolicyForIncomeTaxExpenseTaxCredit': 'Income tax expense comprises current and deferred tax. Current tax and deferred tax are recognised in profit or loss except to the extent that it relates to a business combination, or items recognised directly in equity or in other comprehensive income.\n\nCurrent tax is the expected tax payable or receivable on the taxable income or loss for the year, using tax rates enacted or substantively enacted at the reporting date, and any adjustment to tax payable in respect of previous years.\n\nDeferred tax is recognised in respect of temporary differences between the carrying amounts of assets and liabilities for financial reporting purposes and the amounts used for taxation purposes. Deferred tax is measured at the tax rates that are expected to be applied to temporary differences when they reverse, based on the laws that have been enacted or substantively enacted by the reporting date.\n\nA deferred tax asset is recognised for unused tax losses, tax credits and deductible temporary differences, to the extent that it is probable that future taxable profits will be available against which they can be utilised. Deferred tax assets are reviewed at each reporting date and are reduced to the extent that it is no longer probable that the related tax benefit will be realised.',
      'ssmt:DescriptionOfAccountingPolicyForEmployeeBenefits': 'Short-term employee benefit obligations in respect of salaries, annual bonuses, paid annual leave and sick leave are measured on an undiscounted basis and are expensed as the related service is provided.\n\nThe Company makes contributions to the Employees Provident Fund in Malaysia, a defined contribution pension scheme. Contributions to defined contribution pension schemes are recognised as an expense in the period in which the related service is performed.',
      'ssmt:DescriptionOfAccountingPolicyForProvisionsContingentLiabilitiesAndContingentAssets': 'Provisions are recognised when the Company has a present obligation (legal or constructive) as a result of a past event, it is probable that an outflow of resources embodying economic benefits will be required to settle the obligation and a reliable estimate can be made of the amount of the obligation.\n\nProvisions are reviewed at each reporting date and adjusted to reflect the current best estimate. If it is no longer probable that an outflow of economic resources will be required to settle the obligation, the provision is reversed.\n\nWhere the effect of the time value of money is material, provisions are discounted using a current pre-tax rate that reflects, where appropriate, the risks specific to the liability. When discounting is used, the increase in the provision due to the passage of time is recognised as a finance cost.',
      'ssmt:DescriptionOfAccountingPolicyForLeases': 'At inception of a contract, the Company assesses whether a contract is, or contains, a lease. A contract is, or contains, a lease if the contract conveys the right to control the use of an identified asset for a period of time in exchange for consideration.\n\nThe Company recognises a right-of-use asset and a lease liability at the lease commencement date. The right-of-use asset is initially measured at cost, which comprises the initial amount of the lease liability adjusted for any lease payments made at or before the commencement date, plus any initial direct costs incurred and an estimate of costs to dismantle and remove the underlying asset or to restore the underlying asset or the site on which it is located, less any lease incentives received.\n\nThe right-of-use asset is subsequently depreciated using the straight-line method from the commencement date to the end of the lease term. The lease liability is initially measured at the present value of the lease payments that are not paid at the commencement date, discounted using the interest rate implicit in the lease or, if that rate cannot be readily determined, the Company\'s incremental borrowing rate.\n\nThe Company has elected not to recognise right-of-use assets and lease liabilities for leases of low-value assets and short-term leases. The Company recognises the lease payments associated with these leases as an expense on a straight-line basis over the lease term.',
      'ssmt:DescriptionOfAccountingPolicyForImpairmentOfNonFinancialAssets': 'The carrying amounts of the Company\'s non-financial assets are reviewed at each reporting date to determine whether there is any indication of impairment. If any such indication exists, then the asset\'s recoverable amount is estimated.\n\nFor impairment testing, assets are grouped together into the smallest group of assets that generates cash inflows from continuing use that are largely independent of the cash inflows of other assets or CGUs. The recoverable amount of an asset or CGU is the greater of its value in use and its fair value less costs to sell.\n\nAn impairment loss is recognised if the carrying amount of an asset or CGU exceeds its recoverable amount. Impairment losses are recognised in profit or loss. They are allocated first to reduce the carrying amount of any goodwill allocated to the CGU, and then to reduce the carrying amounts of the other assets in the CGU on a pro rata basis.\n\nAn impairment loss in respect of goodwill is not reversed. For other assets, an impairment loss is reversed only to the extent that the asset\'s carrying amount does not exceed the carrying amount that would have been determined, net of depreciation or amortisation, if no impairment loss had been recognised.',
      'ssmt:DisclosureOfWhetherThereAreOtherAccountingPolicies': 'No'
    };

    this.form.patchValue(demoData);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Accounting policies form has been filled with sample data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    if (confirm('Are you sure you want to clear all data in this form?')) {
      this.form.reset();
      this.stateService.clearSheetData(SHEET_KEYS.notesAccountingPolicies);

      this.messageService.add({
        severity: 'info',
        summary: 'Form Cleared',
        detail: 'All form data has been cleared',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_DEFAULT_MS
      });
      return;
    }

    const formData = this.form.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Accounting policies have been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Setup cross-reference auto-population from Role-1
   * Auto-populates accounting standard, functional and presentation currency
   */
  private setupCrossReferenceAutoPopulation(): void {
    setTimeout(() => {
      // Only auto-populate if fields are empty
      const currentData = this.stateService.getSheetData(SHEET_KEYS.notesAccountingPolicies);
      const isEmpty = !currentData || Object.keys(currentData).length === 0;

      if (isEmpty) {
        // Auto-populate statement of compliance with MFRS/MPERS
        const accountingStandard = this.crossRefService.getCrossReferenceData().accountingStandard;
        if (accountingStandard && this.form.get('ssmt:StatementOfComplianceWithMFRS')) {
          let complianceStatement = '';

          if (accountingStandard === MFRS_ACCOUNTING_STANDARD) {
            complianceStatement = 'The financial statements of the Company have been prepared in accordance with Malaysian Financial Reporting Standards (MFRS) issued by the Malaysian Accounting Standards Board (MASB) and the requirements of the Companies Act 2016 in Malaysia.';
          } else if (accountingStandard === MPERS_ACCOUNTING_STANDARD) {
            complianceStatement = 'The financial statements of the Company have been prepared in accordance with Malaysian Private Entities Reporting Standards (MPERS) issued by the Malaysian Accounting Standards Board (MASB) and the requirements of the Companies Act 2016 in Malaysia.';
          }

          if (complianceStatement) {
            this.form.patchValue({
              'ssmt:StatementOfComplianceWithMFRS': complianceStatement
            }, { emitEvent: false });
          }
        }

        // Auto-populate functional currency from Role-1
        const functionalCurrency = this.crossRefService.getCrossReferenceData().functionalCurrency;
        if (functionalCurrency && this.form.get('ssmt:DescriptionOfFunctionalCurrency')) {
          const currencyDescription = `The functional currency of the Company is ${functionalCurrency === 'MYR' ? 'Ringgit Malaysia (RM)' : functionalCurrency}, which is the currency of the primary economic environment in which the Company operates.`;

          this.form.patchValue({
            'ssmt:DescriptionOfFunctionalCurrency': currencyDescription
          }, { emitEvent: false });
        }

        // Auto-populate presentation currency from Role-1
        const presentationCurrency = this.crossRefService.getCrossReferenceData().presentationCurrency;
        if (presentationCurrency && this.form.get('ssmt:DescriptionOfPresentationCurrency')) {
          const currencyDescription = `These financial statements are presented in ${presentationCurrency === 'MYR' ? 'Ringgit Malaysia (RM)' : presentationCurrency}, which is the Company's functional and presentation currency.`;

          this.form.patchValue({
            'ssmt:DescriptionOfPresentationCurrency': currencyDescription
          }, { emitEvent: false });
        }
      }

      // Subscribe to ongoing changes in Role-1 (reactive updates)
      this.crossRefService.getAccountingStandard$()
        .pipe(takeUntil(this.destroy$))
        .subscribe(accountingStandard => {
          if (accountingStandard && this.form.get('ssmt:StatementOfComplianceWithMFRS')) {
            const currentValue = this.form.get('ssmt:StatementOfComplianceWithMFRS')?.value;
            // Only update if field is empty to avoid overwriting user edits
            if (!currentValue) {
              let complianceStatement = '';

              if (accountingStandard === MFRS_ACCOUNTING_STANDARD) {
                complianceStatement = 'The financial statements of the Company have been prepared in accordance with Malaysian Financial Reporting Standards (MFRS) issued by the Malaysian Accounting Standards Board (MASB) and the requirements of the Companies Act 2016 in Malaysia.';
              } else if (accountingStandard === MPERS_ACCOUNTING_STANDARD) {
                complianceStatement = 'The financial statements of the Company have been prepared in accordance with Malaysian Private Entities Reporting Standards (MPERS) issued by the Malaysian Accounting Standards Board (MASB) and the requirements of the Companies Act 2016 in Malaysia.';
              }

              if (complianceStatement) {
                this.form.patchValue({
                  'ssmt:StatementOfComplianceWithMFRS': complianceStatement
                }, { emitEvent: false });
              }
            }
          }
        });

      this.crossRefService.getPresentationCurrency$()
        .pipe(takeUntil(this.destroy$))
        .subscribe(presentationCurrency => {
          if (presentationCurrency && this.form.get('ssmt:DescriptionOfPresentationCurrency')) {
            const currentValue = this.form.get('ssmt:DescriptionOfPresentationCurrency')?.value;
            if (!currentValue) {
              const currencyDescription = `These financial statements are presented in ${presentationCurrency === 'MYR' ? 'Ringgit Malaysia (RM)' : presentationCurrency}, which is the Company's functional and presentation currency.`;

              this.form.patchValue({
                'ssmt:DescriptionOfPresentationCurrency': currencyDescription
              }, { emitEvent: false });
            }
          }
        });
    }, 400);
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.form.get(qname);
  }

  /** trackBy for the schema-driven *ngFor — avoids DOM rebuilds on each CD pass. */
  trackByQname = (_index: number, control: SchemaControl): string => control.qname;

  /**
   * Check if field is required.
   *
   * Previously this invoked the attached `Validators.required` function on every
   * change-detection tick (called twice per field in the template → 100+ validator
   * runs per CD pass). The validator is attached in buildForm() purely based on
   * `control.required`, so reading the schema flag is equivalent and O(1).
   */
  /**
   * Does this field's label get a `*`?
   *
   * Single-sourced through `isControlMandatory` so every sheet answers this the
   * same way. Each sheet used to hand-roll it and understood only the
   * declaration forms its own schema happened to use, which is how a mechanism
   * moved between sheets could silently stop marking. See `isControlMandatory` in `required-field.util.ts`.
   */
  isFieldRequired(control: SchemaControl): boolean {
    return isControlMandatory(control as any, { selfData: this.form.getRawValue() });
  }

  /**
   * Get error message for a control and error type
   * Uses custom error message from schema if available, otherwise falls back to default
   */
  getErrorMessage(control: SchemaControl, errorType: string): string {
    // Check if control has custom error messages defined in schema
    if (control.error && control.error[errorType]) {
      return control.error[errorType] as string;
    }

    // Fall back to default error messages
    switch (errorType) {
      case 'required':
        return `${control.label} is required`;
      case 'pattern':
        return `Invalid format. ${control.ui.placeholder || ''}`;
      case 'min':
        return `Value must be greater than or equal to 0`;
      case 'max':
        return `Value exceeds maximum allowed`;
      default:
        return 'Invalid value';
    }
  }

  /**
   * Setup warning evaluation for all fields with warnings defined in schema
   */
  private setupWarningEvaluation(): void {
    if (!this.schema?.controls) return;

    // Re-evaluate warnings whenever form values change
    this.form.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // notesAccountingPolicies slice, `applyRemoteChange` rehydrates the
    // shared state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges), which is what makes the local
    // valueChanges-driven re-evaluation above moot here — the field-level
    // warning indicators are refreshed as part of adoption instead.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.notesAccountingPolicies)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesAccountingPolicies)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.form, this.stateService.getSheetData(SHEET_KEYS.notesAccountingPolicies));
    this.evaluateFieldWarnings();
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Called on initial load and on form value changes
   */
  private evaluateFieldWarnings(): void {
    if (!this.schema || !this.form) return;

    // Get all form values for cross-field validations
    const allFormValues = this.form.getRawValue();

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control: SchemaControl) => {
      if ((control as any).warnings && (control as any).warnings.length > 0) {
        const formControl = this.form.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            (control as any).warnings,
            allFormValues,
            true // Always evaluate warnings
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

    // Rebuild the display-cache once per evaluation; template-side lookups become O(1).
    this.rebuildAllWarningsCache();
  }

  /**
   * Build the `<app-warning-message>`-shaped list per field with stable references.
   * Called only from evaluateFieldWarnings().
   */
  private rebuildAllWarningsCache(): void {
    const cache: { [qname: string]: WarningMessage[] } = {};
    for (const qname in this.fieldWarnings) {
      const list = this.fieldWarnings[qname];
      if (!list || list.length === 0) continue;
      const mapped: WarningMessage[] = list.map(warning => ({
        message: warning.message,
        severity: warning.severity === 'error' ? 'error' : 'warning'
      }));
      mapped.sort((firstWarning, secondWarning) => {
        if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
        if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
        return 0;
      });
      cache[qname] = mapped;
    }
    this.allWarningsCache = cache;
  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    return this.allWarningsCache[qname] ?? EMPTY_WARNINGS;
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }
}
